import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { Navigate } from 'react-router-dom';

function PasswordMigration() {
    const { register, handleSubmit, formState: { errors }, getValues, watch } = useForm();
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [countryCode, setCountryCode] = useState('+31');
    
    const password = watch('password');
    
    // Password validation functions
    const validatePassword = (value) => {
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumbers = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
        const hasMinLength = value.length >= 8;
        
        if (!hasMinLength) return "Password must be at least 8 characters long";
        if (!hasUpperCase) return "Password must contain at least one uppercase letter";
        if (!hasLowerCase) return "Password must contain at least one lowercase letter";
        if (!hasNumbers) return "Password must contain at least one number";
        if (!hasSpecialChar) return "Password must contain at least one special character";
        return true;
    };
    
    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) || "Please enter a valid email address";
    };
    
    const validatePhone = (value) => {
        const phoneRegex = /^[0-9]{8,15}$/; // 8-15 digits
        return phoneRegex.test(value) || "Please enter a valid phone number (8-15 digits)";
    };
    
    // Password strength indicator
    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, text: '', color: '' };
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
        
        if (score <= 2) return { strength: score * 20, text: 'Weak', color: 'danger' };
        if (score <= 3) return { strength: score * 20, text: 'Fair', color: 'warning' };
        if (score <= 4) return { strength: score * 20, text: 'Good', color: 'info' };
        return { strength: score * 20, text: 'Strong', color: 'success' };
    };
    
    const passwordStrength = getPasswordStrength(password);
    
    const countryOptions = [
        { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
        { code: '+1', name: 'United States', flag: '🇺🇸' },
        { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
        { code: '+49', name: 'Germany', flag: '🇩🇪' },
        { code: '+33', name: 'France', flag: '🇫🇷' },
        { code: '+32', name: 'Belgium', flag: '🇧🇪' },
        { code: '+34', name: 'Spain', flag: '🇪🇸' },
        { code: '+39', name: 'Italy', flag: '🇮🇹' },
        { code: '+46', name: 'Sweden', flag: '🇸🇪' },
        { code: '+47', name: 'Norway', flag: '🇳🇴' },
        { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    ];

    function submit(data) {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authentication token not found. Please login again.');
            return;
        }
        
        const bodyFormData = new FormData();
        bodyFormData.append("newPassword", data.password);
        bodyFormData.append("email", data.email);
        bodyFormData.append("phoneNumber", countryCode + data.phoneNumber);
        
        axios.post("/auth/migrate-password", bodyFormData, {
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
                'Authorization': `Bearer ${token}`
            }
        })
        .then(response => {
            if (response.status === 200) {
                setSuccess('Password and contact information updated successfully!');
                localStorage.removeItem('requiresPasswordMigration');
                
                // Check if user is a teacher and needs 2FA setup
                const userRole = localStorage.getItem('role');
                if (userRole?.includes('TEACHER')) {
                    setTimeout(() => {
                        window.location.href = '/2fa-setup';
                    }, 2000);
                } else {
                    setTimeout(() => {
                        setIsCompleted(true);
                    }, 2000);
                }
            }
        })
        .catch(err => {
            console.error('Migration error:', err);
            if (err.response?.status === 401) {
                setError('Session expired. Please login again.');
            } else if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('An error occurred while updating your information. Please try again.');
            }
        });
    }

    if (isCompleted) {
        const userRole = localStorage.getItem('role');
        const userId = localStorage.getItem('id');
        
        if (userRole === 'TEACHER') {
            return <Navigate to="/" />;
        } else if (userRole === 'STUDENT') {
            return <Navigate to={`/student/${userId}`} />;
        } else {
            return <Navigate to="/login" />;
        }
    }

    return (
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
            <Card>
                <Card.Body>
                    <Card.Title className="text-center mb-4">Security Update Required</Card.Title>
                    
                    <Alert variant="info" className="mb-4">
                        <Alert.Heading>Account Security Update</Alert.Heading>
                        <p>
                            To enhance the security of your account, please set a new password and provide 
                            your contact information. This will help us keep your account secure and enable 
                            important security features like two-factor authentication.
                        </p>
                    </Alert>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}
                    
                    <Form onSubmit={handleSubmit(submit)}>
                        {/* New Password */}
                        <Form.Group className="mb-3" controlId="formPassword">
                            <Form.Label>New Password *</Form.Label>
                            <Form.Control 
                                {...register("password", { 
                                    required: "Password is required",
                                    validate: validatePassword
                                })} 
                                type="password" 
                                placeholder="Enter new password" 
                            />
                            {password && (
                                <div className="mt-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <small>Password Strength: <span className={`text-${passwordStrength.color}`}>{passwordStrength.text}</span></small>
                                    </div>
                                    <div className="progress" style={{ height: '5px' }}>
                                        <div 
                                            className={`progress-bar bg-${passwordStrength.color}`}
                                            style={{ width: `${passwordStrength.strength}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                            <Form.Text className="text-muted">
                                Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                            </Form.Text>
                            {errors.password && <div className="text-danger mt-1">{errors.password.message}</div>}
                        </Form.Group>

                        {/* Confirm Password */}
                        <Form.Group className="mb-3" controlId="formPasswordConfirm">
                            <Form.Label>Confirm New Password *</Form.Label>
                            <Form.Control 
                                {...register("passwordConfirm", {
                                    required: "Please confirm your password",
                                    validate: value => value === getValues().password || "Passwords do not match"
                                })}
                                type="password" 
                                placeholder="Confirm new password" 
                            />
                            {errors.passwordConfirm && <div className="text-danger mt-1">{errors.passwordConfirm.message}</div>}
                        </Form.Group>

                        {/* Email */}
                        <Form.Group className="mb-3" controlId="formEmail">
                            <Form.Label>Email Address *</Form.Label>
                            <Form.Control 
                                {...register("email", { 
                                    required: "Email is required",
                                    validate: validateEmail
                                })} 
                                type="email" 
                                placeholder="Enter email address" 
                            />
                            <Form.Text className="text-muted">
                                We'll use this email for security notifications and account recovery.
                            </Form.Text>
                            {errors.email && <div className="text-danger mt-1">{errors.email.message}</div>}
                        </Form.Group>

                        {/* Phone Number with Country Code */}
                        <Form.Group className="mb-4" controlId="formPhoneNumber">
                            <Form.Label>Phone Number *</Form.Label>
                            <InputGroup>
                                <Form.Select 
                                    value={countryCode} 
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    style={{ maxWidth: '150px' }}
                                >
                                    {countryOptions.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.flag} {country.code}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control 
                                    {...register("phoneNumber", { 
                                        required: "Phone number is required",
                                        validate: validatePhone
                                    })} 
                                    type="tel" 
                                    placeholder="123456789" 
                                />
                            </InputGroup>
                            <Form.Text className="text-muted">
                                We'll use this number for two-factor authentication and security alerts.
                            </Form.Text>
                            {errors.phoneNumber && <div className="text-danger mt-1">{errors.phoneNumber.message}</div>}
                        </Form.Group>

                        <div className="d-grid">
                            <Button variant="primary" type="submit" size="lg">
                                Update Security Information
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
}

export default PasswordMigration;